import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";

const FOCUSABLE = 'a[href], button:not([disabled])';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-12 items-center rounded-lg px-3 text-sm font-medium ${
    isActive
      ? "bg-indigo-950 text-indigo-200"
      : "text-slate-300 hover:bg-slate-800 hover:text-white"
  }`;

/** Small-screen admin navigation with modal focus and scroll behavior. */
export function AdminMobileNav({
  open,
  onClose,
  role,
}: {
  open: boolean;
  onClose: () => void;
  role?: string;
}) {
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const canManage = role === "admin" || role === "manager";
  const isAdmin = role === "admin";

  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-black/70 backdrop-blur-sm"
      />
      <section
        id="admin-mobile-nav"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Admin navigation"
        className="absolute inset-y-0 left-0 flex w-[88%] max-w-sm flex-col overflow-y-auto border-r border-slate-800 bg-slate-950 px-4 pb-6 pt-4 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-800 pb-4">
          <div className="flex min-w-0 items-center gap-2">
            <img src="/logo-mark.svg" alt="" className="h-8 w-8 rounded-md bg-white p-0.5" />
            <span className="truncate text-sm font-semibold text-slate-100">Farnsworth Admin</span>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400"
          >
            <span aria-hidden="true" className="text-xl">×</span>
          </button>
        </div>
        <nav aria-label="Mobile primary" className="mt-4">
          <ul className="space-y-1">
            <li><NavLink to="/" end onClick={onClose} className={linkClass}>Inventory</NavLink></li>
            <li><NavLink to="/reports" onClick={onClose} className={linkClass}>Reports</NavLink></li>
            {canManage ? <li><NavLink to="/whatnot" onClick={onClose} className={linkClass}>Whatnot</NavLink></li> : null}
            {canManage ? <li><NavLink to="/categories" onClick={onClose} className={linkClass}>Categories</NavLink></li> : null}
            {canManage ? <li><NavLink to="/retailers" onClick={onClose} className={linkClass}>Retailers</NavLink></li> : null}
            {canManage ? <li><NavLink to="/messages" onClick={onClose} className={linkClass}>Messages</NavLink></li> : null}
            {isAdmin ? <li><NavLink to="/users" onClick={onClose} className={linkClass}>Users</NavLink></li> : null}
            {isAdmin ? <li><NavLink to="/audit" onClick={onClose} className={linkClass}>Audit history</NavLink></li> : null}
            {isAdmin ? <li><NavLink to="/agent-keys" onClick={onClose} className={linkClass}>Agent keys</NavLink></li> : null}
          </ul>
        </nav>
      </section>
    </div>,
    document.body,
  );
}

